import { useState, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import {
    Backdrop,
    Paper,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Divider,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell
} from "@mui/material";

import { UserContext } from "../../context/UserContext";
import { handleNumberInput } from "../utils";
import CustomModalHeader from "../custom_components/CustomModalHeader";
import CustomButton from "../custom_components/CustomButton";

import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const phpFmt = (n) =>
    Number(n ?? 0).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

// ─── Single order row in the left panel ──────────────────────────────────────

function OrderRow({ order, onRemove }) {
    const total = Number(order.Price ?? 0) + Number(order.SFee ?? 0);
    const paid = Number(order.PaidAmount ?? 0);
    const balance = Number(order.ItemBalance ?? total - paid);

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FAFBFC",
                transition: "border-color .15s",
                
                "&:hover": { borderColor: "#94A3B8" },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1} minWidth={0}>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#0F172A",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 160,
                        }}
                    >
                        {order.ItemName}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: "#94A3B8",
                            fontFamily: "monospace",
                            mt: 0.25,
                        }}
                    >
                        {order.OrderID}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    onClick={() => onRemove(order.id)}
                    sx={{
                        width: 22,
                        height: 22,
                        color: "#CBD5E1",
                        "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" },
                    }}
                >
                    <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Stack>

            {/* Amount rows */}
            <Table
                size="small"
                sx={{
                    mt: 1,
                    width: "100%",
                    borderRadius: 1,
                    "& th, & td": {
                        py: 0.2,
                        px: 0.8,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                    },
                }}
            >
                <TableHead>
                    <TableRow
                        sx={{
                            "& th": {
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: "grey.100",
                                fontFamily: "monospace",
                            },
                        }}
                    >
                        {["Total", "Paid", "Balance"].map((header) => (
                            <TableCell key={header}>
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    <TableRow>
                        {[
                            { value: total, color: "#1E293B" },
                            { value: paid, color: "#059669" },
                            { value: balance, color: "#DC2626" },
                        ].map(({ value, color }, index) => (
                            <TableCell
                                key={index}
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color,
                                    fontFamily: "monospace",
                                }}
                            >
                                ₱{phpFmt(value)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddMultiPayment({
    open,
    onClose,
    onUpdate,
    customerId = "",
    selectedOrders = [],
}) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { paymentMethods } = useContext(UserContext);

    // Left panel — mutable local copy so user can deselect rows
    const [orders, setOrders] = useState(selectedOrders);

    useEffect(() => {
        if (open) {
            setOrders(selectedOrders);
        }
    }, [open, selectedOrders]);

    // Right panel — payment form
    const [paidAmount, setPaidAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentRemarks, setPaymentRemarks] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // Derived totals from remaining orders
    const totalBalance = orders.reduce(
        (s, o) => s + Number(o.ItemBalance ?? (Number(o.Price ?? 0) + Number(o.SFee ?? 0) - Number(o.PaidAmount ?? 0))),
        0
    );
    const totalPaid = orders.reduce((s, o) => s + Number(o.PaidAmount ?? 0), 0);
    const totalAmt = orders.reduce((s, o) => s + Number(o.Price ?? 0) + Number(o.SFee ?? 0), 0);

    const handleRemoveOrder = (id) => setOrders((prev) => prev.filter((o) => o.id !== id));

    // SAVE PAYMENTS FOR SELECTED ORDERS
    const handleSavePayments = async () => {
        if (!customerId) {
            Swal.fire({ text: "Customer ID not found.", icon: "warning" });
            return;
        }
        if (!orders.length) {
            Swal.fire({ text: "No orders selected.", icon: "warning" });
            return;
        }
        if (!paidAmount || !paymentMethod || !paymentDate) {
            Swal.fire({ text: "Please complete all required fields.", icon: "warning" });
            return;
        }

        const toInsertOrders = orders.map((o) => ({
            OrderID: o.OrderID,
            Balance: Number(o.ItemBalance),
            Amount: 0,
        }));

        const paymentResult = distributePayment(paidAmount, toInsertOrders);

        setIsSaving(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_payment/insert_bulk_payment.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        CustomerId: customerId,
                        orders: paymentResult.allocations,
                        PaidAmount: paidAmount,
                        PaymentMethod: paymentMethod,
                        PaymentDate: paymentDate,
                        PaymentRemarks: paymentRemarks,
                    }),
                }
            );

            const result = await response.json();

            if (result.status === "success") {

                const appliedByOrderId = Object.fromEntries(
                    paymentResult.allocations.map((a) => [a.OrderID, a])
                );

                const updatedOrders = orders.map((order) => {
                    const allocation = appliedByOrderId[order.OrderID];

                    if (!allocation) return order;

                    return {
                        OrderID: allocation.OrderID,
                        PaidAmount: allocation.Amount + order.PaidAmount,
                        ItemBalance: allocation.NewBalance,
                        PaymentStatus: allocation.PaymentStatus,
                    };
                });

                onUpdate(updatedOrders);

                handleClose();

            } else {
                Swal.fire({
                    icon: "error",
                    text: result.message,
                });
            }
        } catch (error) {
            console.error(error);

            Swal.fire({
                title: "Error saving payment!",
                text: String(error),
                icon: "error",
            });
        } finally {
            setIsSaving(false);
        }

    };

    // DISTRIBUTE PAYMENT TO ORDERS
    function distributePayment(paidAmount, orders, roundTo = 100) {
        const toWhole = (n) => Math.max(0, Math.round(Number(n) || 0));

        const total = toWhole(paidAmount);
        const balances = orders.map((o) => toWhole(o.Balance));
        const totalBalance = balances.reduce((sum, b) => sum + b, 0);

        const getStatus = (paid, balance) => {
            if (balance <= 0) return 'Paid';
            if (paid <= 0) return 'Pending';
            if (paid >= balance) return 'Paid';
            return 'Partially';
        };

        // Case 1: Payment covers everything — no rounding needed, pay exact balances.
        if (total >= totalBalance) {
            const allocations = orders.map((order, i) => ({
                OrderID: order.OrderID,
                Amount: balances[i],
                NewBalance: 0,
                PaymentStatus: getStatus(balances[i], balances[i]),
            }));
            return { allocations, RemainingPayment: total - totalBalance };
        }

        // Case 2: Proportional split, rounded to the nearest `roundTo` unit,
        // then leftover units are nudged onto the orders with room to absorb them
        // so the total always matches `paidAmount` exactly.
        const rawShares = balances.map((b) => (b / totalBalance) * total);
        const rounded = rawShares.map((share, i) => {
            const r = Math.round(share / roundTo) * roundTo;
            return Math.min(r, balances[i]); // never exceed that order's own balance
        });

        let diff = total - rounded.reduce((sum, a) => sum + a, 0);

        // Distribute any leftover/excess in `roundTo`-sized steps, largest balance first.
        const order = [...rounded.keys()].sort((a, b) => balances[b] - balances[a]);
        let guard = 0;
        while (diff !== 0 && guard < 10000) {
            for (const i of order) {
                if (diff === 0) break;
                const step = diff > 0 ? Math.min(roundTo, diff) : Math.max(-roundTo, diff);
                const room = balances[i] - rounded[i];
                if (step > 0 && room <= 0) continue;
                if (step < 0 && rounded[i] <= 0) continue;
                const applied = step > 0 ? Math.min(step, room) : Math.max(step, -rounded[i]);
                rounded[i] += applied;
                diff -= applied;
            }
            guard++;
        }

        const allocations = orders.map((order, i) => ({
            OrderID: order.OrderID,
            Amount: rounded[i],
            NewBalance: balances[i] - rounded[i],
            PaymentStatus: getStatus(rounded[i], balances[i]),
        }));

        return { allocations, RemainingPayment: 0 };
    };

    const handleClose = () => {
        if (isSaving) return;
        setOrders(selectedOrders);
        setPaidAmount("");
        setPaymentMethod("");
        setPaymentDate("");
        setPaymentRemarks("");
        onClose();
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{ zIndex: (t) => t.zIndex.drawer + 1, backdropFilter: "blur(1px)" }}
        >
            <Paper
                sx={{
                    width: { xs: "95vw", sm: 780 },
                    maxHeight: "95vh",
                    borderRadius: "12px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",

                    // Top accent bar
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0, left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489",
                        zIndex: 1,
                    },
                }}
            >
                {/* ── Modal header ── */}
                <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
                    <CustomModalHeader
                        title="Add Payment"
                        subtitle={`Apply payment across ${orders.length} selected order${orders.length !== 1 ? "s" : ""}`}
                        onClose={handleClose}
                    />
                </Box>

                {/* ── Two-column body ── */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    sx={{ flex: 1, overflow: "hidden" }}
                >

                    {/* ════════════════════════════════
                        LEFT — Selected orders list
                    ════════════════════════════════ */}
                    <Box
                        sx={{
                            width: { xs: "100%", sm: 380 },
                            flexShrink: 0,
                            borderRight: { sm: "1px solid #F1F5F9" },
                            display: "flex",
                            flexDirection: "column",
                            bgcolor: "#F8FAFC",
                        }}
                    >
                        {/* Section label */}
                        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #F1F5F9" }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ReceiptLongOutlinedIcon sx={{ fontSize: 15, color: "#64748B" }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B", letterSpacing: 0.6, textTransform: "uppercase" }}>
                                    Selected Orders
                                </Typography>
                                <Box sx={{
                                    ml: "auto",
                                    px: 1, py: "1px",
                                    borderRadius: "20px",
                                    bgcolor: "#E2E8F0",
                                }}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>
                                        {orders.length}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Order rows */}
                        <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
                            {orders.length === 0 ? (
                                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 4 }} spacing={1}>
                                    <ReceiptLongOutlinedIcon sx={{ fontSize: 30, color: "#CBD5E1" }} />
                                    <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>No orders selected</Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    {orders.map((order) => (
                                        <OrderRow
                                            key={order.id}
                                            order={order}
                                            onRemove={handleRemoveOrder}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>

                    {/* ════════════════════════════════
                        RIGHT — Payment form
                    ════════════════════════════════ */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

                        {/* Section label */}
                        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #F1F5F9", bgcolor: "#fff" }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748B", letterSpacing: 0.6, textTransform: "uppercase" }}>
                                Payment Information
                            </Typography>
                        </Box>

                        {/* Form */}
                        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "#fff", }}>
                            <Stack spacing={2} sx={{ px: 2.5, py: 2.5 }}>
                                {/* Paid Amount */}
                                <TextField
                                    autoComplete="off"
                                    label="Paid Amount"
                                    size="small"
                                    value={paidAmount}
                                    onChange={(e) => handleNumberInput(e, setPaidAmount)}
                                    fullWidth
                                />

                                {/* Payment Method */}
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select
                                        value={paymentMethod}
                                        label="Payment Method"
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        {paymentMethods.map((pm) => (
                                            <MenuItem
                                                key={pm.id}
                                                value={pm.id}
                                                sx={{ py: 1.25 }}
                                            >
                                                <Stack spacing={0.5} width="100%">
                                                    <Typography
                                                        fontSize={12}
                                                        fontWeight={600}
                                                    >
                                                        {pm.AccType}
                                                    </Typography>

                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {`${pm.AccName} • ${pm.AccNumber}`}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Payment Date */}
                                <TextField
                                    autoComplete="off"
                                    label="Payment Date"
                                    type="date"
                                    size="small"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />

                                {/* Remarks */}
                                <TextField
                                    autoComplete="off"
                                    label="Remarks"
                                    size="small"
                                    value={paymentRemarks}
                                    onChange={(e) => setPaymentRemarks(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </Stack>
                        </Box>

                        {/* Action buttons */}
                        {/* <Box sx={{
                            px: 2.5, py: 2,
                            borderTop: "1px solid #F1F5F9",
                            bgcolor: "#fff",
                            flexShrink: 0,
                        }}>
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                <CustomButton
                                    label="Cancel"
                                    variant="outlined"
                                    hoverColor="#d14249"
                                    onClick={handleClose}
                                />
                                <CustomButton
                                    label={isSaving ? "Saving..." : "Save Payment"}
                                    disabled={isSaving || !orders.length}
                                    onClick={handleSavePayments}
                                />
                            </Stack>
                        </Box> */}

                    </Box>

                </Stack>

                {/* Summary footer */}
                <Box sx={{
                    px: 2, py: 1.5,
                    borderTop: "1px solid #E2E8F0",
                    bgcolor: "#fff",
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Stack direction="column" sx={{ minWidth: 270 }}>
                        {[
                            { label: "Total Amount", value: phpFmt(totalAmt), color: "#1E293B" },
                            { label: "Total Paid", value: phpFmt(totalPaid), color: "#059669" },
                            { label: "Total Balance", value: phpFmt(totalBalance), color: "#DC2626" },
                        ].map(({ label, value, color }) => (
                            <Stack
                                key={label}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                py={0.4}
                            >
                                <Typography sx={{ fontSize: 11, color: "#64748B" }}>{label}</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color, fontFamily: "monospace" }}>
                                    ₱{value}
                                </Typography>
                            </Stack>
                        ))}

                        {/* Balance vs entered amount indicator */}
                        {paidAmount && (
                            <>
                                <Divider sx={{ my: 0.75, borderColor: "#F1F5F9" }} />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>Remaining after payment</Typography>
                                    <Typography sx={{
                                        fontSize: 12, fontWeight: 800, fontFamily: "monospace",
                                        color: totalBalance - Number(paidAmount) < 0 ? "#DC2626" : "#0369A1",
                                    }}>
                                        ₱{phpFmt(Math.max(0, totalBalance - Number(paidAmount)))}
                                    </Typography>
                                </Stack>
                            </>
                        )}
                    </Stack>

                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <CustomButton
                            label="Cancel"
                            variant="outlined"
                            hoverColor="#d14249"
                            onClick={handleClose}
                        />
                        <CustomButton
                            label={isSaving ? "Saving..." : "Save Payment"}
                            disabled={isSaving || !orders.length}
                            onClick={handleSavePayments}
                        />
                    </Stack>
                </Box>

            </Paper>

        </Backdrop>
    );
}